/**
 * Accueil du site public (maquette validée par Guillaume le 08/10/2026) :
 * calculs PURS, sans base ni cache Next, importables depuis le serveur, une
 * route d'API ou un composant navigateur. Testés par
 * scripts/tests/accueil.test.ts.
 *
 *  — `familleProjet` / `comptesParFamille` : les quatre familles de la carte
 *    de l'accueil (Forêt, Agriculture, Haies et vergers, Bâtiment), comptées
 *    sur le catalogue public, jamais saisies à la main ;
 *  — `choisirProjets` : les trois projets réels proposés à la fin du
 *    questionnaire d'orientation (type, puis proximité des sites, puis
 *    volume). La disponibilité n'est PAS utilisée : au 08/10/2026 le champ
 *    `volumeDisponible` du catalogue vaut 100 pour les 341 projets publiés,
 *    il ne dit donc rien de la disponibilité réelle ;
 *  — `messageQuestionnaire` : le message enregistré dans la collection
 *    `contacts` (réponses et projets proposés), lu par l'équipe.
 */
import { DEPT_BY_CODE } from '../../data/departements-fr'
import { FORESTRY_TYPES } from '../../data/project-types'

export type Locale = 'fr' | 'en'

/* ─────────────── Familles de la carte ─────────────── */

export type FamilleCarte = 'foret' | 'agriculture' | 'haies-vergers' | 'batiment'

export const FAMILLES_CARTE: readonly FamilleCarte[] = ['foret', 'agriculture', 'haies-vergers', 'batiment']

/** Couleurs des points (charte du site : vert forêt, ambre, vert clair, gris). */
export const COULEURS_FAMILLE: Record<FamilleCarte, string> = {
  foret: '#1B4D3E',
  agriculture: '#D4A24C',
  'haies-vergers': '#3E9C7A',
  batiment: '#6B7A72',
}

export const LIBELLES_FAMILLE: Record<FamilleCarte, Record<Locale, string>> = {
  foret: { fr: 'Forêt', en: 'Forest' },
  agriculture: { fr: 'Agriculture', en: 'Farming' },
  'haies-vergers': { fr: 'Haies et vergers', en: 'Hedges and orchards' },
  batiment: { fr: 'Bâtiment', en: 'Building' },
}

const HAIES_VERGERS = ['haies', 'vergers']

/** Famille d'un projet d'après son type (méthode du Label Bas-Carbone). */
export function familleProjet(type: string): FamilleCarte {
  if (FORESTRY_TYPES.includes(type)) return 'foret'
  if (HAIES_VERGERS.includes(type)) return 'haies-vergers'
  if (type === 'batiment') return 'batiment'
  return 'agriculture'
}

/** Nombre de projets par famille (toutes les familles présentes, zéro compris). */
export function comptesParFamille(projets: readonly { type: string }[]): Record<FamilleCarte, number> {
  const c: Record<FamilleCarte, number> = { foret: 0, agriculture: 0, 'haies-vergers': 0, batiment: 0 }
  for (const p of projets) c[familleProjet(p.type)] += 1
  return c
}

/* ─────────────── Questionnaire d'orientation ─────────────── */

export type Objectif = 'compenser' | 'equipes' | 'reporting' | 'proximite'
export type Volume = 'moins-100' | '100-1000' | '1000-10000' | 'plus-10000' | 'inconnu'
export type FamilleVoulue = 'foret' | 'agriculture' | 'les-deux'
export type Echeance = 'cette-annee' | 'annee-prochaine' | 'a-definir'

export interface Choix<T extends string> {
  valeur: T
  fr: string
  en: string
  /** Précision affichée sous le libellé. */
  detailFr?: string
  detailEn?: string
}

export const OBJECTIFS: Choix<Objectif>[] = [
  { valeur: 'compenser', fr: 'Compenser nos émissions', en: 'Offset our emissions' },
  { valeur: 'equipes', fr: 'Engager nos équipes', en: 'Engage our teams' },
  { valeur: 'reporting', fr: 'Préparer notre reporting', en: 'Prepare our reporting' },
  { valeur: 'proximite', fr: 'Agir près de nos sites', en: 'Act close to our sites' },
]

export const VOLUMES: Choix<Volume>[] = [
  { valeur: 'moins-100', fr: 'Moins de 100 tCO₂ par an', en: 'Under 100 tCO₂ a year', detailFr: 'TPE, PME', detailEn: 'Small business' },
  { valeur: '100-1000', fr: '100 à 1 000 tCO₂ par an', en: '100 to 1,000 tCO₂ a year', detailFr: 'PME, ETI', detailEn: 'SME, mid-size company' },
  { valeur: '1000-10000', fr: '1 000 à 10 000 tCO₂ par an', en: '1,000 to 10,000 tCO₂ a year', detailFr: 'ETI, grand groupe', detailEn: 'Mid-size company, large group' },
  { valeur: 'plus-10000', fr: 'Plus de 10 000 tCO₂ par an', en: 'Over 10,000 tCO₂ a year', detailFr: 'Grand groupe', detailEn: 'Large group' },
  { valeur: 'inconnu', fr: 'Je ne sais pas encore', en: "I don't know yet", detailFr: 'Nous vous aiderons à l’estimer', detailEn: 'We will help you estimate it' },
]

export const FAMILLES_VOULUES: Choix<FamilleVoulue>[] = [
  { valeur: 'foret', fr: 'Forêt', en: 'Forest', detailFr: 'Boisement, reboisement', detailEn: 'Afforestation, reforestation' },
  { valeur: 'agriculture', fr: 'Agriculture', en: 'Farming', detailFr: 'Élevage, grandes cultures, haies, vergers', detailEn: 'Livestock, field crops, hedges, orchards' },
  { valeur: 'les-deux', fr: 'Les deux', en: 'Both', detailFr: 'Un portefeuille équilibré', detailEn: 'A balanced portfolio' },
]

export const ECHEANCES: Choix<Echeance>[] = [
  { valeur: 'cette-annee', fr: 'D’ici la fin de l’année', en: 'By the end of the year' },
  { valeur: 'annee-prochaine', fr: 'L’an prochain', en: 'Next year' },
  { valeur: 'a-definir', fr: 'À définir', en: 'Not decided yet' },
]

export interface ReponsesQuestionnaire {
  objectif: Objectif
  volume: Volume
  famille: FamilleVoulue
  /** Régions choisies (noms du catalogue) ; vide = partout en France. */
  regions: string[]
  /** Codes postaux des sites (5 chiffres). */
  codesPostaux: string[]
  echeance: Echeance
}

const est = <T extends string>(liste: Choix<T>[], v: unknown): v is T => liste.some((c) => c.valeur === v)

/** Codes postaux lus dans une saisie libre (« 44000, 35200 75008 ») : 5 chiffres, sans doublon, 10 au plus. */
export function lireCodesPostaux(saisie: string): string[] {
  return [...new Set((saisie.match(/\b\d{5}\b/g) ?? []))].slice(0, 10)
}

/**
 * Réponses reçues par l'API, validées une à une : toute valeur inconnue rend
 * null (la route répond alors 400). Les régions doivent appartenir à la liste
 * fournie (celles du catalogue).
 */
export function validerReponses(brut: unknown, regionsConnues: readonly string[]): ReponsesQuestionnaire | null {
  if (!brut || typeof brut !== 'object') return null
  const b = brut as Record<string, unknown>
  if (!est(OBJECTIFS, b.objectif) || !est(VOLUMES, b.volume) || !est(FAMILLES_VOULUES, b.famille) || !est(ECHEANCES, b.echeance)) return null
  const regions = Array.isArray(b.regions) ? b.regions.filter((r): r is string => typeof r === 'string' && regionsConnues.includes(r)) : []
  const codes = Array.isArray(b.codesPostaux) ? lireCodesPostaux(b.codesPostaux.filter((c) => typeof c === 'string').join(' ')) : []
  return { objectif: b.objectif, volume: b.volume, famille: b.famille, echeance: b.echeance, regions: [...new Set(regions)].slice(0, 13), codesPostaux: codes }
}

/** Département d'un code postal (Corse : 2A sous 20200, 2B au-delà). */
export function departementDuCodePostal(cp: string): string | null {
  if (!/^\d{5}$/.test(cp)) return null
  if (cp.startsWith('20')) return Number(cp) < 20200 ? '2A' : '2B'
  if (cp.startsWith('97') || cp.startsWith('98')) return null
  return cp.slice(0, 2)
}

/** Distance à vol d'oiseau, en kilomètres. */
export function distanceKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const r = Math.PI / 180
  const dLat = (b.lat - a.lat) * r
  const dLon = (b.lon - a.lon) * r
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLon / 2) ** 2
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** Ce que le choix lit d'un projet du catalogue public. */
export interface ProjetQuestionnaire {
  slug: string
  title: string
  type: string
  region: string
  commune?: string | null
  co2Total: number
  coords: { lat: number; lon: number } | null
}

export interface ProjetPropose<P extends ProjetQuestionnaire = ProjetQuestionnaire> {
  projet: P
  /** Distance au site le plus proche (centre du département de chaque code postal, km arrondis), null sans code postal. */
  distanceKm: number | null
  /** true : le projet est dans une des régions choisies. */
  dansRegion: boolean
}

const cle = (r: string) => r.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[\s-]+/g, ' ').trim()

/** Le projet correspond-il à la famille voulue ? Le bâtiment n'est jamais proposé par le questionnaire. */
export function projetDeLaFamille(type: string, voulue: FamilleVoulue): boolean {
  const f = familleProjet(type)
  if (f === 'batiment') return false
  if (voulue === 'les-deux') return true
  if (voulue === 'foret') return f === 'foret'
  return f === 'agriculture' || f === 'haies-vergers'
}

/** Gros volume annoncé : on préfère, à proximité égale, les projets qui ont le plus de tonnes attendues. */
const grosVolume = (v: Volume) => v === '1000-10000' || v === 'plus-10000'

/** Tranches de distance dans lesquelles deux projets sont jugés « aussi proches » l'un que l'autre. */
const TRANCHE_KM = 60

/**
 * Les `n` projets réels qui correspondent le mieux aux réponses.
 *
 *  1. Famille voulue (forêt, agriculture, les deux ; jamais le bâtiment).
 *  2. Localisation, si elle est donnée : un projet situé dans une région
 *     choisie passe d'abord ; puis la distance au point le plus proche
 *     (centre du département de chaque code postal, centre des projets de
 *     chaque région choisie), par tranches de 60 km.
 *  3. À proximité égale : pour un gros volume (≥ 1 000 t/an), les projets
 *     aux plus fortes tonnes attendues ; sinon, le plus proche.
 *  4. Diversité : « les deux » garantit au moins un projet forestier et un
 *     agricole ; on évite deux projets de la même commune s'il y a mieux.
 *
 * Sans localisation, les projets sont classés par tonnes attendues.
 */
export function choisirProjets<P extends ProjetQuestionnaire>(
  catalogue: readonly P[],
  r: Pick<ReponsesQuestionnaire, 'famille' | 'regions' | 'codesPostaux' | 'volume'>,
  n = 3,
): ProjetPropose<P>[] {
  const regionsVoulues = new Set(r.regions.map(cle))
  // Sites (codes postaux) : servent au tri ET à la distance affichée « à N km de vos sites ».
  const sites: { lat: number; lon: number }[] = []
  for (const cp of r.codesPostaux) {
    const d = departementDuCodePostal(cp)
    const dept = d ? DEPT_BY_CODE[d] : undefined
    if (dept) sites.push({ lat: dept.lat, lon: dept.lon })
  }
  // Centres des régions choisies : servent au tri seulement (ce ne sont pas des sites).
  const points: { lat: number; lon: number }[] = [...sites]
  for (const reg of regionsVoulues) {
    const dans = catalogue.filter((p) => p.coords && cle(p.region) === reg)
    if (dans.length) {
      points.push({
        lat: dans.reduce((a, p) => a + p.coords!.lat, 0) / dans.length,
        lon: dans.reduce((a, p) => a + p.coords!.lon, 0) / dans.length,
      })
    }
  }
  const localise = points.length > 0 || regionsVoulues.size > 0

  const candidats: (ProjetPropose<P> & { tri: number | null })[] = catalogue
    .filter((p) => projetDeLaFamille(p.type, r.famille))
    .filter((p) => !localise || p.coords)
    .map((p) => {
      const tri = points.length && p.coords ? Math.min(...points.map((pt) => distanceKm(pt, p.coords!))) : null
      const auxSites = sites.length && p.coords ? Math.min(...sites.map((pt) => distanceKm(pt, p.coords!))) : null
      return {
        projet: p,
        distanceKm: auxSites == null ? null : Math.round(auxSites),
        dansRegion: regionsVoulues.has(cle(p.region)),
        tri: tri == null ? null : Math.round(tri),
      }
    })

  const gros = grosVolume(r.volume)
  candidats.sort((a, b) => {
    if (localise) {
      if (a.dansRegion !== b.dansRegion) return a.dansRegion ? -1 : 1
      const ta = Math.floor((a.tri ?? 0) / TRANCHE_KM)
      const tb = Math.floor((b.tri ?? 0) / TRANCHE_KM)
      if (ta !== tb) return ta - tb
      if (!gros && a.tri !== b.tri) return (a.tri ?? 0) - (b.tri ?? 0)
    }
    if (b.projet.co2Total !== a.projet.co2Total) return b.projet.co2Total - a.projet.co2Total
    return a.projet.title.localeCompare(b.projet.title) || a.projet.slug.localeCompare(b.projet.slug)
  })

  type Candidat = (typeof candidats)[number]
  const retenus: Candidat[] = []
  const prendre = (c: Candidat | undefined) => { if (c && !retenus.includes(c) && retenus.length < n) retenus.push(c) }
  const commune = (c: Candidat) => cle(c.projet.commune ?? '') || c.projet.slug
  const meilleur = (filtre: (c: Candidat) => boolean) => {
    const libres = candidats.filter((c) => !retenus.includes(c) && filtre(c))
    return libres.find((c) => !retenus.some((x) => commune(x) === commune(c))) ?? libres[0]
  }

  if (r.famille === 'les-deux') {
    prendre(meilleur(() => true))
    const famillesPrises = new Set(retenus.map((c) => familleProjet(c.projet.type) === 'foret'))
    prendre(meilleur((c) => !famillesPrises.has(familleProjet(c.projet.type) === 'foret')))
  }
  while (retenus.length < n) {
    const c = meilleur(() => true)
    if (!c) break
    prendre(c)
  }
  // Ordre d'affichage : celui du classement
  return retenus
    .sort((a, b) => candidats.indexOf(a) - candidats.indexOf(b))
    .map(({ projet, distanceKm, dansRegion }) => ({ projet, distanceKm, dansRegion }))
}

/* ─────────────── Message enregistré dans `contacts` ─────────────── */

export const SUJET_QUESTIONNAIRE = 'devis_questionnaire'

const libelle = <T extends string>(liste: Choix<T>[], v: T) => liste.find((c) => c.valeur === v)?.fr ?? v

/**
 * Message de la demande : réponses en clair, puis projets proposés avec le
 * lien de leur fiche. Toujours en français (lu par l'équipe).
 */
export function messageQuestionnaire(
  r: ReponsesQuestionnaire,
  projets: readonly { titre: string; slug: string; methode: string; region: string; tco2: number }[],
  site = 'https://www.stock-co2.com',
): string {
  const fmt = new Intl.NumberFormat('fr-FR')
  const ou = [
    r.regions.length ? `régions : ${r.regions.join(', ')}` : '',
    r.codesPostaux.length ? `codes postaux : ${r.codesPostaux.join(', ')}` : '',
  ].filter(Boolean).join(' ; ') || 'partout en France'
  return [
    'Demande issue du questionnaire de l’accueil.',
    '',
    `Attente principale : ${libelle(OBJECTIFS, r.objectif)}`,
    `Volume annuel envisagé : ${libelle(VOLUMES, r.volume)}`,
    `Type de projets : ${libelle(FAMILLES_VOULUES, r.famille)}`,
    `Localisation : ${ou}`,
    `Échéance : ${libelle(ECHEANCES, r.echeance)}`,
    '',
    'Projets proposés :',
    ...(projets.length
      ? projets.map((p, i) => `${i + 1}. ${p.titre} (${p.methode}, ${p.region}, ${fmt.format(Math.round(p.tco2))} tCO₂ attendues) : ${site}/projets/${p.slug}`)
      : ['aucun projet ne correspondait aux critères']),
  ].join('\n')
}
